import React, { FunctionComponent, useCallback } from 'react';
import Flex from 'components/Flex';
import Text from 'components/Text';
import Button from 'components/Button';
import useZendeskWidget from 'hooks/useZendeskWidget';
import useProfile from 'hooks/useProfile';
import useNavigator from 'hooks/useNavigator';
import useUser from 'hooks/useUser';
import styled from 'styled-components';

interface ContactSupportViewProps {
  step: number;
  goToNextStep: () => void;
  goToPrevStep: () => void;
  onClose: () => void;
}

const View: FunctionComponent<ContactSupportViewProps> = ({
  step,
  goToNextStep,
  goToPrevStep,
  onClose,
}) => {
  const {
    isReady: isSupportWidgetReady,
    prefillWidget,
    showWidget,
    openWidget,
    openContactForm,
  } = useZendeskWidget({ hideLauncher: true });

  const { hasAccess, isNoProfile } = useProfile();
  const { navigateCallback } = useNavigator();
  const { user, fullName } = useUser(false);

  const openRequestsForm = useCallback(() => {
    if (!isNoProfile && hasAccess('requests', 'create')) {
      onClose();
      navigateCallback('/requests/create')();
    } else {
      goToNextStep();
    }
  }, [isNoProfile, hasAccess, navigateCallback, goToNextStep, onClose]);

  const openSupportWidget = useCallback(() => {
    if (!isSupportWidgetReady) return;

    openContactForm();

    if (fullName || user?.email) {
      prefillWidget({
        ...(fullName && { name: { value: fullName, readOnly: false } }),
        ...(user.email && { email: { value: user.email, readOnly: false } }),
      });
    }

    showWidget();
    openWidget();
    onClose();
  }, [
    isSupportWidgetReady,
    openContactForm,
    prefillWidget,
    openWidget,
    fullName,
    user?.email,
    onClose,
  ]);

  if (step === 2) {
    return (
      <Flex direction="column" alignItems="center">
        <Text align="center" className="pb8">
          You have just contacted the CondoGenie support team. We provide our service to your
          condominium and are not part of your condominium association, condo board, or management
          company.
        </Text>
        <Text align="center" className="pb8">
          To contact your condo board or management you can use the Requests page if available on
          the main menu.
        </Text>
        <Text align="center">
          If the Requests page is not available then you'll have to contact them directly outside of
          your CondoGenie portal.
        </Text>
        <Button styleType="secondary" className="mt16" onClick={goToPrevStep}>
          Back
        </Button>
      </Flex>
    );
  }

  return (
    <Flex wrap="wrap" justifyContent="center" alignItems="center">
      <StyledContactButton styleType="blueFill" className="m16" onClick={openRequestsForm}>
        I would like to contact my condo board or management
      </StyledContactButton>
      <StyledContactButton
        styleType="blueFill"
        className="m16"
        disabled={!isSupportWidgetReady}
        onClick={openSupportWidget}
      >
        I would like to contact CondoGenie Support
      </StyledContactButton>
    </Flex>
  );
};

export default View;

const StyledContactButton = styled(Button)`
  border-radius: 8px;
  max-width: 275px;
`;
